
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/components/ui/use-toast";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import ArticleStats from "@/components/article/ArticleStats";
import { ArticleStatsService } from "@/services/ArticleStatsService";
import { useTimedAccess } from "@/context/TimedAccessContext";
import PremiumContentOverlay from "@/components/article/PremiumContentOverlay";
import { SUPABASE_FUNCTIONS } from "@/utils/supabaseConfig";
import { getArticleBySlugSync } from "@/data/articles/index";
import FeedbackSection from "@/components/article/FeedbackSection";
import CommentSection from "@/components/article/CommentSection";
import MetaTags from "@/components/MetaTags";
import LoadingState from "@/components/article/LoadingState";

const SUPABASE_FUNCTION_URL =
  "https://mtkkozkxmfywpibzjttu.functions.supabase.co/get-article-storage";
const ArticleHarmfulContentDetection = () => {
  const slug = "harmful-content-detection";
  const articleData = getArticleBySlugSync(slug);
  const [htmlContent, setHtmlContent] = useState<string | null>(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [initialReadCount, setInitialReadCount] = useState<number>();
  const [initialLikeCount, setInitialLikeCount] = useState<number>();

  const { isSubscribed, user, isPremiumLoading } = useAuth();
  const { hasFreeAccess } = useTimedAccess();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const isPremium = articleData?.isPremium ?? true;

  useEffect(() => {
    const loadStats = async () => {
      const stats = await ArticleStatsService.fetchStats(slug);
      setInitialReadCount(stats.readCount);
      setInitialLikeCount(stats.likeCount);
    };

    loadStats();
  }, [slug]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const preview = searchParams.get("preview") === "true" || searchParams.get("test") === "true";
    setIsPreviewMode(preview);

    if (!preview && !user) {
      navigate("/login", { state: { returnTo: location.pathname } });
      return;
    }

    if (preview && !isSubscribed) {
      toast({
        title: "Preview Mode",
        description: "You're viewing this premium article in preview mode. Subscribe for full access.",
      });
    }

    if (isSubscribed || preview || hasFreeAccess) {
      const url = `${SUPABASE_FUNCTION_URL}?slug=${slug}`;
      console.log("📦 Fetching article from:", url);

      fetch(url)
        .then((res) => {
          console.log("✅ Fetch response status:", res.status);
          if (!res.ok) {
            throw new Error(`Failed to fetch article: ${res.status} ${res.statusText}`);
          }
          return res.text();
        })
        .then((html) => {
          console.log("📝 Article loaded successfully");
          setHtmlContent(html);
        })
        .catch((error) => {
          console.error("Error fetching article from Supabase:", error);
          setHtmlContent("<p>Sorry, this article could not be loaded at this time.</p>");
        });
    }
  }, [isSubscribed, hasFreeAccess, user, navigate, location.pathname, toast]);

  const breadcrumbs = [
    { name: "Home", item: "/" },
    { name: "Learn", item: "/learn" },
    { name: "ML System Design", item: "/learn" },
    { name: articleData?.title || "Evolution of Candidate Generation Algorithms", item: location.pathname },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <MetaTags
        title={articleData?.title || "Evolution of Candidate Generation Algorithms"}
        description={articleData?.description || "Learn about how candidate generation has evolved in recommendation systems."}
        articleData={{
          publishDate: articleData?.publishDate || "2024-05-01",
          author: articleData?.author || "HelloML",
          category: articleData?.category || "ML System Design",
        }}
        breadcrumbs={breadcrumbs}
      />
      <Navbar />

      <main className="flex-grow py-8 md:py-12">
        <div className="container max-w-3xl">
          <div className="mb-8">
            <Link to="/learn">
              <Button variant="ghost" className="pl-0 flex items-center gap-2">
                <ArrowLeft size={16} />
                Back to resources
              </Button>
            </Link>
          </div>

          <article className="prose prose-lg max-w-none">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">
              {articleData?.title || "Evolution of Candidate Generation Algorithms"}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-6">
              <span>{articleData?.readTime || "15 min read"}</span>
              <span>{articleData?.category || "Recommendation Systems"}</span>
              <ArticleStats
                articleId={slug}
                initialReadCount={initialReadCount}
                initialLikeCount={initialLikeCount}
              />
            </div>

            <div className="relative">
              {isPremiumLoading && <LoadingState />}
              {!isPremiumLoading && !isSubscribed && !hasFreeAccess && !isPreviewMode && (
                <div className="mt-8 text-center">
                  <div className="p-6 bg-muted rounded-lg">
                    <h3 className="text-xl font-semibold mb-2">Premium Content</h3>
                    <p className="text-muted-foreground mb-4">
                      This article is premium content. Subscribe for full access.
                    </p>
                    <div className="flex flex-col sm:flex-row justify-center gap-4">
                      <Link to="/get-premium">
                        <Button>Get Premium Access</Button>
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              {(isSubscribed || hasFreeAccess || isPreviewMode) && !isPremiumLoading && (
                htmlContent ? (
                  <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
                ) : (
                  <LoadingState />
                )
              )}

              {isPremium && !isSubscribed && !hasFreeAccess && isPreviewMode && !isPremiumLoading && <PremiumContentOverlay />}
            </div>
          </article>

          <FeedbackSection articleTitle={articleData?.title || "Evolution of Candidate Generation Algorithms"} articleId="candidate-generation" />

          {(isSubscribed || hasFreeAccess || isPreviewMode) && (
            <CommentSection articleId="candidate-generation" />
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ArticleHarmfulContentDetection;
